// src/contentful/queries.js
// GraphQL query definitions for Contentful
// Updated to match actual Contentful schema

import { gql } from 'graphql-request';

// Fragment for asset fields (images)
const ASSET_FIELDS = `
  fragment AssetFields on Asset {
    sys {
      id
    }
    url
    title
    description
    width
    height
    contentType
  }
`;

// Fragment for author fields (bio is Rich Text, omitted for list views)
const AUTHOR_FIELDS = `
  fragment AuthorFields on Author {
    sys {
      id
    }
    name
    role
    avatar {
      url
      title
      description
    }
    linkedInUrl
  }
`;

// Fragment for topic fields
const TOPIC_FIELDS = `
  fragment TopicFields on Topic {
    sys {
      id
    }
    name
    slug
    tags
    description
  }
`;

/**
 * Query to fetch all resources (articles + case studies)
 * Used on the main Resources page
 *
 * Schema notes:
 * - Article: Has isFeatured boolean, NO tagsCollection
 * - CaseStudy: Has isFeatured boolean, NO region, NO heroImage, metrics is JSON scalar
 */
export const RESOURCES_LIST_QUERY = gql`
  ${ASSET_FIELDS}
  ${AUTHOR_FIELDS}
  ${TOPIC_FIELDS}

  query ResourcesList($articleLimit: Int = 50, $caseStudyLimit: Int = 50) {
    articleCollection(limit: $articleLimit, order: publishedDate_DESC) {
      total
      items {
        sys {
          id
        }
        slug
        title
        heroIntro
        category
        publishedDate
        readingTime
        isFeatured
        heroImage {
          ...AssetFields
        }
        author {
          ...AuthorFields
        }
        topicsCollection(limit: 5) {
          items {
            ...TopicFields
          }
        }
      }
    }

    caseStudyCollection(limit: $caseStudyLimit, order: sys_publishedAt_DESC) {
      total
      items {
        sys {
          id
        }
        slug
        title
        heroIntro
        publishedDate
        isFeatured
        clientName
        industry
        headlineOutcome
        metrics
        topicsCollection(limit: 5) {
          items {
            ...TopicFields
          }
        }
      }
    }
  }
`;

/**
 * Query to fetch a single article by slug
 * Used on Article detail page
 *
 * Schema notes:
 * - Has isFeatured boolean
 * - NO updatedAt, NO tagsCollection, NO relatedArticlesCollection
 */
export const ARTICLE_BY_SLUG_QUERY = gql`
  ${ASSET_FIELDS}
  ${AUTHOR_FIELDS}
  ${TOPIC_FIELDS}

  query ArticleBySlug($slug: String!) {
    articleCollection(where: { slug: $slug }, limit: 1) {
      items {
        sys {
          id
        }
        slug
        title
        heroIntro
        category
        publishedDate
        readingTime
        isFeatured
        heroImage {
          ...AssetFields
        }
        author {
          ...AuthorFields
        }
        body {
          json
          links {
            assets {
              block {
                sys {
                  id
                }
                url
                title
                description
                width
                height
                contentType
              }
            }
            entries {
              block {
                sys {
                  id
                }
                __typename
              }
              inline {
                sys {
                  id
                }
                __typename
              }
            }
          }
        }
        topicsCollection(limit: 5) {
          items {
            ...TopicFields
          }
        }
      }
    }
  }
`;

/**
 * Query to fetch a single case study by slug
 * Used on Case Study detail page
 *
 * Schema notes:
 * - NO region, NO heroImage, NO challenge/approach/results rich text
 * - NO metricsCollection, NO technologiesCollection, NO testimonial
 * - NO relatedCaseStudiesCollection
 * - metrics is JSON scalar
 */
export const CASE_STUDY_BY_SLUG_QUERY = gql`
  ${TOPIC_FIELDS}

  query CaseStudyBySlug($slug: String!) {
    caseStudyCollection(where: { slug: $slug }, limit: 1) {
      items {
        sys {
          id
        }
        slug
        title
        heroIntro
        publishedDate
        isFeatured
        clientName
        industry
        headlineOutcome
        metrics
        topicsCollection(limit: 5) {
          items {
            ...TopicFields
          }
        }
      }
    }
  }
`;

/**
 * Query to fetch related resources by topics
 * Used for "Related Resources" sections
 */
export const RELATED_RESOURCES_QUERY = gql`
  query RelatedResources($excludeId: String!, $limit: Int = 3) {
    articleCollection(
      where: {
        sys: { id_not: $excludeId },
        topicsCollection_exists: true
      },
      limit: $limit,
      order: publishedDate_DESC
    ) {
      items {
        sys {
          id
        }
        slug
        title
        heroIntro
        category
        publishedDate
        readingTime
        heroImage {
          url
          description
        }
      }
    }
  }
`;
